// Sales-rep variant of Order Intake — lightweight "submit as deal" form.
// Unified manufacturer submission: routes to dealer-create or legacy POS handoff.

import { SectionCard } from "@/components/common/SectionCard";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import {
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import type { OpsOrderIntakeSalePath } from "@/lib/orderIntakeClient";

import { CUSTOMER_SALE_PATHS } from "./salesRepIntakeBuild";
import { SalesRepSubmittedOrdersList } from "./SalesRepSubmittedOrdersList";
import { useSalesRepIntakeForm } from "./useSalesRepIntakeForm";
import { IntakePlantPacketUrlFields } from "@/components/manufacturing/IntakePlantPacketUrlFields";

type Props = {
  /** When false, hide the in-panel submissions list (dealer portal Place Order). */
  showSubmissionsList?: boolean;
  /** Optional outer SectionCard title override. */
  formTitle?: string;
  formDescription?: string;
};

export function SalesRepIntakeForm({
  showSubmissionsList = true,
  formTitle = "Submit customer order",
  formDescription = "Creates an order for review — your org or an upstream manufacturer. Plant, model, and paperwork can be completed at accept time.",
}: Props = {}) {
  const f = useSalesRepIntakeForm();

  return (
    <>
      <div {...{ [FIREFLY_TARGET_ATTR]: "intake-form" }}>
        <SectionCard title={formTitle} description={formDescription}>
          <div className="space-y-4">
        {f.prefillLeadId ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-indigo-200 bg-indigo-50/80 px-3 py-2 text-xs text-indigo-900 dark:border-indigo-800 dark:bg-indigo-950/35 dark:text-indigo-100">
            <span>
              Prefilled from lead{" "}
              <span className="font-mono">{f.prefillLeadId.slice(0, 8)}</span>
              {f.prefillLinkedOpportunityId ? (
                <>
                  {" "}
                  · linked opportunity{" "}
                  <span className="font-mono">{f.prefillLinkedOpportunityId.slice(0, 8)}</span>
                </>
              ) : null}{" "}
              — review fields, then submit or dismiss.
            </span>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={f.submitBusy}
              onClick={f.dismissPrefill}
            >
              Dismiss prefill
            </button>
          </div>
        ) : null}

        {f.catalogBusy ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">Loading locations…</p>
        ) : null}
        {f.catalogError ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100">
            {f.catalogError}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Customer name" required>
            <input
              className={inputClass}
              value={f.draft.customer.name}
              onChange={(e) => f.patchCustomer({ name: e.target.value })}
              disabled={f.submitBusy}
              autoComplete="name"
            />
          </Field>
          <Field label="Phone">
            <input
              className={inputClass}
              type="tel"
              value={f.draft.customer.phone}
              onChange={(e) => f.patchCustomer({ phone: e.target.value })}
              disabled={f.submitBusy}
              autoComplete="tel"
            />
          </Field>
          <Field label="Email">
            <input
              className={inputClass}
              type="email"
              value={f.draft.customer.email}
              onChange={(e) => f.patchCustomer({ email: e.target.value })}
              disabled={f.submitBusy}
              autoComplete="email"
            />
          </Field>
          <Field label="Address">
            <AddressAutocompleteInput
              value={f.draft.customer.address}
              onChange={(next) => f.patchCustomer({ address: next })}
              onPick={(pick) => f.patchCustomer({ address: pick.formatted })}
              disabled={f.submitBusy}
              className={inputClass}
              placeholder="Start typing install address…"
            />
          </Field>

          <Field label="Sale path" required>
            <select
              className={inputClass}
              value={f.draft.salePath}
              onChange={(e) =>
                f.patchDraft({ salePath: e.target.value as OpsOrderIntakeSalePath | "" })
              }
              disabled={f.submitBusy}
            >
              <option value="" disabled>
                Select sale path
              </option>
              {CUSTOMER_SALE_PATHS.map((sp) => (
                <option key={sp.id} value={sp.id}>
                  {sp.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Sale amount (optional)">
            <input
              className={inputClass}
              type="number"
              min={0}
              step="0.01"
              value={f.draft.saleAmountUsd}
              onChange={(e) => f.patchDraft({ saleAmountUsd: e.target.value })}
              disabled={f.submitBusy}
              placeholder="12000"
            />
          </Field>

          <Field label="Seller location" required={f.submittingToManufacturer}>
            <select
              className={inputClass}
              value={f.draft.sellerLocationId}
              onChange={(e) => f.patchDraft({ sellerLocationId: e.target.value })}
              disabled={f.submitBusy || f.catalogBusy}
            >
              <option value="" disabled>
                {f.locations.length ? "Select location" : "No locations found"}
              </option>
              {f.locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </Field>

          {f.hasManufacturerPartners ? (
            <Field label="Manufacturer" className="sm:col-span-2">
              <select
                className={inputClass}
                value={f.draft.destinationManufacturerOrgId}
                onChange={(e) => f.patchDraft({ destinationManufacturerOrgId: e.target.value })}
                disabled={f.submitBusy || f.catalogBusy}
              >
                <option value="">This organization (internal order)</option>
                {f.manufacturers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {f.submittingToManufacturer ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                  Submits to the manufacturer for review when you click Submit order.
                </p>
              ) : null}
            </Field>
          ) : null}
        </div>

        <IntakePlantPacketUrlFields
          orderFormUrl={f.draft.orderFormUrl}
          floorPlanUrl={f.draft.floorPlanUrl}
          onOrderFormUrl={(orderFormUrl) => f.patchDraft({ orderFormUrl })}
          onFloorPlanUrl={(floorPlanUrl) => f.patchDraft({ floorPlanUrl })}
          disabled={f.submitBusy}
        />

        <Field label="Notes (optional)">
          <textarea
            className={`${inputClass} min-h-[72px]`}
            value={f.draft.notes}
            onChange={(e) => f.patchDraft({ notes: e.target.value })}
            placeholder="Colour, options, delivery notes — anything the reviewer should know."
            disabled={f.submitBusy}
          />
        </Field>

        {f.submitError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {f.submitError}
          </div>
        ) : null}
        {f.submitSuccess ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100">
            <p>{f.submitSuccess}</p>
            {f.legacyPosHandoffUrl ? (
              <p className="mt-2">
                <a
                  href={f.legacyPosHandoffUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline"
                >
                  Open POS
                </a>
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            {...{ [FIREFLY_TARGET_ATTR]: "intake-submit-order" }}
            className={primaryBtnClass}
            disabled={f.submitBusy || f.catalogBusy}
            onClick={() => {
              void f.submit();
            }}
          >
            {f.submitBusy ? "Submitting…" : "Submit order"}
          </button>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={f.submitBusy}
            onClick={() => {
              void f.reloadCatalog();
            }}
          >
            Refresh
          </button>
        </div>
          </div>
        </SectionCard>
      </div>

      {showSubmissionsList ? (
        <div {...{ [FIREFLY_TARGET_ATTR]: "intake-submitted-list" }}>
          <SalesRepSubmittedOrdersList
            refreshKey={f.submissionsRefreshKey}
            manufacturers={f.manufacturers}
          />
        </div>
      ) : null}
    </>
  );
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block text-xs text-gray-600 dark:text-zinc-300 ${className ?? ""}`}>
      <span className="block">
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </span>
      <span className="mt-1 block">{children}</span>
    </label>
  );
}

